import { useMemo } from 'react';
import { Search } from 'lucide-react';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Pager } from '../../../components/ui/Pager';
import { type MobLite } from '../../../components/admin/UserMobilisations';
import { UserRow } from './UserRow';
import { type AdminUser } from './roles';

const PAGE_SIZE = 25;

/**
 * One tab's worth of users: a name/email search box, the table itself,
 * and a pager. Filtering and paging are client-side over the already
 * fetched list (the whole cohort is small enough to hold in memory);
 * this mirrors the /admin/people pattern. Search + page state live in
 * UsersAdmin so switching tabs can reset them.
 */
export function UsersTabContent({
  users,
  allMobs,
  meId,
  editing,
  onEdit,
  onSaved,
  q,
  onQ,
  page,
  onPage,
  noun,
}: {
  users: AdminUser[];
  allMobs: MobLite[];
  meId: string;
  editing: string | null;
  onEdit: (id: string | null) => void;
  onSaved: (u: AdminUser) => void;
  q: string;
  onQ: (value: string) => void;
  page: number;
  onPage: (page: number) => void;
  /** Plural noun for the pager count line, e.g. "inductees". */
  noun: string;
}) {
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) =>
        u.display_name.toLowerCase().includes(needle) ||
        u.email.toLowerCase().includes(needle),
    );
  }, [users, q]);

  // Clamp rather than reset in an effect: a shrinking result set (e.g.
  // narrowing the search) lands on the last valid page with no flicker.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <>
      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-dim" />
        <input
          type="search"
          value={q}
          onChange={(e) => onQ(e.target.value)}
          placeholder="Search by name or email..."
          className="block h-12 w-full rounded-lg border border-line-strong/20 bg-surface pl-11 pr-3 text-base shadow-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
        />
      </div>

      <div className="mt-3 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
        {filtered.length === 0 ? (
          <EmptyState
            icon={<Search className="h-6 w-6" />}
            title="No matches"
            description={`Nothing for "${q}". Check spelling or clear the search.`}
          />
        ) : (
          <UsersTable
            users={pageRows}
            allMobs={allMobs}
            meId={meId}
            editing={editing}
            onEdit={onEdit}
            onSaved={onSaved}
          />
        )}
      </div>

      <Pager
        page={safePage}
        limit={PAGE_SIZE}
        total={filtered.length}
        onPage={onPage}
        noun={noun}
      />
    </>
  );
}

function UsersTable({
  users,
  allMobs,
  meId,
  editing,
  onEdit,
  onSaved,
}: {
  users: AdminUser[];
  allMobs: MobLite[];
  meId: string;
  editing: string | null;
  onEdit: (id: string | null) => void;
  onSaved: (u: AdminUser) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-muted">
          <tr>
            <th className="px-4 py-2.5">User</th>
            <th className="px-4 py-2.5">Roles</th>
            <th className="px-4 py-2.5">Mobilisations</th>
            <th className="px-4 py-2.5">Linked attendee</th>
            <th className="px-4 py-2.5">Login</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              allMobs={allMobs}
              isMe={meId === u.id}
              isEditing={editing === u.id}
              onEdit={() => onEdit(u.id)}
              onCancel={() => onEdit(null)}
              onSaved={onSaved}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
